import { useBusinessServiceRequestWithFirstQueryLoading } from '@ingenico/react-section-utils';
import { defaultSearchByColumns } from '@ingenico/util-common';
import { isEmpty, toString } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { DeviceManagementService } from '../../domain/business/services/deviceManagement.service';
import { useLogicalDeviceApplicationColumns } from '../../hooks/useLogicalDeviceApplicationColumns.hook';
import { useDeviceManagementError } from '../../hooks/useDeviceManagementError.hook';
import type { UseLogicalDeviceApplicationsCardReturn } from './logicalDeviceApplicationsCard.model';

export function useLogicalDeviceApplicationsCard({
  shouldFetch = false,
}: {
  readonly shouldFetch?: boolean;
}): UseLogicalDeviceApplicationsCardReturn {
  const { logicalDeviceId } = useParams<{ logicalDeviceId: string }>();
  const [searchValue, setSearchValue] = useState<string>('');

  const memorizedLogicalDeviceId = useMemo(() => toString(logicalDeviceId), [logicalDeviceId]);

  /* apis */
  const softwareQuery = useBusinessServiceRequestWithFirstQueryLoading(
    DeviceManagementService,
    useCallback(
      (deviceManagementService, isRefetch) =>
        memorizedLogicalDeviceId && shouldFetch
          ? deviceManagementService.getSoftwareListByLogicalDeviceId({
              logicalDeviceId: memorizedLogicalDeviceId,
              isRefetch,
            })
          : null,
      [memorizedLogicalDeviceId, shouldFetch],
    ),
  );
  const [appsError, setAppsError] = useState(softwareQuery.error);

  const [latestSuccessfullApplications, setLatestSuccessfullApplications] = useState(softwareQuery.data);
  useEffect(() => {
    if (softwareQuery.isSuccess) {
      setLatestSuccessfullApplications(softwareQuery.data);
      setAppsError(null);
    }
  }, [softwareQuery.data, softwareQuery.error, softwareQuery.isSuccess]);

  useEffect(() => {
    if (softwareQuery.isError && latestSuccessfullApplications === null) {
      setAppsError(softwareQuery.error);
    }
  }, [latestSuccessfullApplications, softwareQuery.error, softwareQuery.isError]);

  const applications = useMemo(
    () => latestSuccessfullApplications?.applications,
    [latestSuccessfullApplications?.applications],
  );
  const isApplicationError = useMemo(
    () => softwareQuery?.isError && isEmpty(latestSuccessfullApplications),
    [latestSuccessfullApplications, softwareQuery?.isError],
  );

  //=============== input search ===================
  const handleInputSearchChange = useCallback((value: string) => setSearchValue(value), []);

  const searchInput = useMemo<UseLogicalDeviceApplicationsCardReturn['searchInput']>(
    () => ({
      value: searchValue,
      handleChange: handleInputSearchChange,
    }),
    [searchValue, handleInputSearchChange],
  );

  // ================= grid =================
  const { columns, defaultAvailableColumns } = useLogicalDeviceApplicationColumns();

  const rows = useMemo(() => {
    return defaultSearchByColumns(applications ?? [], defaultAvailableColumns, searchValue);
  }, [applications, searchValue, defaultAvailableColumns]);

  const { getErrorMessage } = useDeviceManagementError();

  return {
    grid: {
      columns,
      rows,
    },
    searchInput,
    isApplicationError,
    appsError,
    getErrorMessage,
    refetch: softwareQuery.refetch,
    isFirstLoading: softwareQuery.isFirstLoading,
  };
}
